import { useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { removeYakurekiFromLocalCache } from '@/shared/apis/yakurekiSync'

/**
 * 開いていた薬歴が消えたら (usePollEvent の isGone) 一覧から外し、取り直してから landing へ戻す。
 *
 * 破棄でフリー録音の event が削除されると (ADR-0039) 以降この画面は 404 になる。usePollEvent が
 * polling を止め、ここで着地させる = 戻り先の無い画面に留めない。破棄以外の 404 (別端末で
 * 削除された薬歴・orphan sweep) も同じ扱い: 無い薬歴の空画面に留めるより landing に選び直させる。
 *
 * 404 は削除の信号でもある。一覧の delta 同期 (updated_at 起点) は hard delete を運べないので、
 * 別端末の破棄や sweep で消えた薬歴は、この端末の一覧 (永続キャッシュ) に手動リロードまで残る。
 * 同一端末の破棄は cancel 応答の deleted_event_ids で外しているが、それ以外の削除を front が
 * 知る唯一の機会がこの 404 なので、ここで名指しで外してから取り直す。
 *
 * 一覧を先に取り直すのは landing のため。landing は一覧の cache (queryClient 既定 staleTime 30 秒)
 * からそのまま送り先を決めるので、古い snapshot に消えた event が残っていると '/' と 404 を
 * 往復する。同一端末の破棄は useRecordingStatus.discardRecording が先に取り直しているが、
 * 別端末の破棄や sweep の 404 はここしか通らない。
 */
export const useGoneRedirect = (
  isGone: boolean,
  yakurekiId: string | null
): void => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!isGone) return
    let cancelled = false
    if (yakurekiId) removeYakurekiFromLocalCache([yakurekiId])
    // 取り直しの成否に関わらず戻る (失敗しても 404 の画面に留めない)。
    // 取り直し中に利用者が別画面へ移っていたら (unmount) 引き戻さない。
    const back = () => {
      if (!cancelled) navigate('/', { replace: true })
    }
    queryClient
      .invalidateQueries({ queryKey: ['yakurekiList'], refetchType: 'all' })
      .then(back, back)
    return () => {
      cancelled = true
    }
  }, [isGone, yakurekiId, navigate, queryClient])
}
